import type { CameraData, Model3D, ModelNode, NodeFile } from '@/entities/model3d'
import type { Analytics } from '@/plugins/analytics'
import { exhaustive } from '@/lib/three-d-resources/utils'
import type { ModelResponse } from '@/lib/three-d-resources/gltf-loader'
import { isDracoWASMError, loadGltf } from '@/lib/three-d-resources/gltf-loader'
import type { AnimationClip, Group, Material, Object3D, Texture } from 'three'
import {
  BackSide,
  Cache,
  Color,
  DoubleSide,
  FrontSide,
  Light,
  LinearFilter,
  LinearSRGBColorSpace,
  Mesh,
  MeshBasicMaterial,
  WebGLRenderer,
  NormalBlending,
  AdditiveBlending,
  SubtractiveBlending,
  MultiplyBlending,
  MeshStandardMaterial,
  EquirectangularReflectionMapping,
  Euler,
} from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'

Cache.enabled = true

let hdrTexture: Texture
const MODEL_LOAD_ATTEMPTS = 4

new RGBELoader().load('/shipwreck_at_the_rocky_ocean_coast_1k.hdr', (texture: Texture) => {
  texture.mapping = EquirectangularReflectionMapping
  hdrTexture = texture
})

export type ModelType = {
  modelId: number
  nodeId: number | null
  url: string
  nodes: Map<number, ModelNode> // Keyed by node id
  sceneNodes: Map<number, Object3D> // Keyed by node id
  animations: Array<AnimationClip>
  nodeNameToId: Map<string, number>
  group: Object3D
  billboardLabels: Object3D[]
  meta: {
    hasLighting: boolean
  }
}

export class GLTFMissingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GLTFMissingError'
  }
}

export class MemoryError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MemoryError'
  }
}

export class CancelEventError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CancelEventError'
  }
}

export type LoaderConfig = {
  // axios: NuxtAxiosInstance
  analytics: typeof Analytics | null
  uuid: string | null
  accessType?: 'edit' | 'view'
  maxAge?: number
}

const getCapabilities = (() => {
  let capabilities: { maxAnisotropy: number } | null = null

  return () => {
    if (capabilities) {
      return capabilities
    }

    const featureDetectionRenderer = new WebGLRenderer()
    capabilities = {
      maxAnisotropy: featureDetectionRenderer.capabilities.getMaxAnisotropy(),
    }
    featureDetectionRenderer.dispose()
    return capabilities
  }
})()

type MaterialWithMap = Material & {
  map: Texture
}

const setMaterialTextureProperties = (material: MaterialWithMap): MaterialWithMap => {
  if (material.map) {
    material.map.anisotropy = getCapabilities().maxAnisotropy
    // -- chooses the 4 closets pixels from a texture and blends them in the appropriate proportions relative the proximity of each pixel
    material.map.minFilter = LinearFilter
    material.map.colorSpace = LinearSRGBColorSpace
  }
  material.needsUpdate = true
  material.alphaTest = 0.01

  return material
}

const setMaterialProperties = (mat: Material, node: ModelNode): Material => {
  // "transparency" property is a new field that takes priority. If the value is 1 (always 1 on old models)
  // we'll go ahead and use the fallback opacity. Fallback will default to 1 as well if unset

  // note, with regards to transparency, in three.js, opaque objects are typically rendered before transparent objects
  const material = node.materials[0]
  const opacity = material && material.transparency !== 1 ? material.transparency : mat.opacity

  switch (material.cull_type) {
    case 'B':
      mat.side = FrontSide
      break
    case 'F':
      mat.side = BackSide
      break
    case 'N':
      mat.side = DoubleSide
      break
  }

  // the default case for blend_mode === 'A'
  mat.blending = NormalBlending
  mat.transparent = opacity > 0 && opacity < 1
  mat.opacity = opacity

  switch (material.blend_mode) {
    case 'D':
      mat.blending = AdditiveBlending
      mat.transparent = true
      break
    case 'U':
      mat.blending = SubtractiveBlending
      mat.transparent = true
      break
    case 'L':
      mat.blending = MultiplyBlending
      mat.transparent = true
      break
  }

  mat.needsUpdate = true

  return mat
}

const loadModelResource = async (
  nodeFile: NodeFile,
  modelData: Model3D,
  onProgress: (event: ProgressEvent) => void,
  { analytics, uuid, accessType }: { analytics?: typeof Analytics | null; uuid: string | null; accessType?: 'edit' | 'view' },
): Promise<ModelType> => {
  try {
    const response = await loadModelWithRetry(nodeFile, onProgress, { analytics, uuid })
    let group: Group

    switch (modelData.version) {
      case 1:
        group = response.scene
        break
      case 2:
        group = (response.scene?.children[0]?.children[0] || response.scene.children[0]) as Group
        break
      default:
        exhaustive(modelData.version)
    }

    const animations = response.animations
    const sceneNodes = new Map<number, Object3D>()
    const nodes = new Map<number, ModelNode>()
    const nodeNameToId = new Map<string, number>()
    modelData.nodes.forEach(node => {
      nodeNameToId.set(node.node_name, node.id)
      nodes.set(node.id, node)
    }, {})

    const billboardLabels: Object3D[] = []
    let hasLighting = false
    group.traverse((child: Object3D) => {
      if (child instanceof Light) {
        hasLighting = true
      }
      if (child instanceof Mesh) {
        // better hit tests on v2 models only
        if (modelData.version === 2 && accessType === 'edit') {
          child.geometry.computeBoundsTree()
        }

        // NOTE: If there are bugs from changing opacity on shared materials, we can bring this code back.
        // Ensure unique materials on meshes. This used to happen for us in the export pipeline but doesn't anymore
        // We need unique materials so we can fade models in/out that share materials in the GLTF since THREE.js
        // has opacity on the material instance, not per Mesh
        // if (materialSet.has(child.material)) {
        //   child.material = child.material.clone()
        // } else {
        //   materialSet.add(child.material)
        // }
      }

      const nodeId = nodeNameToId.get(child.name)
      if (!nodeId) {
        if (child instanceof Mesh && child.material.map) {
          child.material.map.colorSpace = LinearSRGBColorSpace
          child.material.needsUpdate = true
        }
        return
      }

      child.userData.nodeId = nodeId

      const node = nodes.get(nodeId)

      if (node?.billboard) {
        billboardLabels.push(child)
      }

      // if (animations.length === 0 && !node?.billboard) {
      //   child.matrixAutoUpdate = false
      // }

      if (!(child instanceof Mesh)) {
        sceneNodes.set(nodeId, child)
        return
      }

      try {
        if (!node) {
          if (child.material.map) {
            child.material.map.colorSpace = LinearSRGBColorSpace
            child.material.needsUpdate = true
          }
          return
        }

        const { diffuse, lighting_model, material_file_id } = node.materials[0]

        if (lighting_model === 'C') {
          child.material = new MeshBasicMaterial({ map: child.material.map })
        }

        if (diffuse.includes('url') || !!material_file_id) {
          // -- when loading a DRACO model's textures, we need to manually set the format to RGBA
          child.material.envMap = hdrTexture

          const m =
            lighting_model === 'R'
              ? new MeshStandardMaterial({
                  envMap: hdrTexture,
                  map: child.material.map,
                  roughnessMap: child.material.roughnessMap,
                  metalnessMap: child.material.metalnessMap,
                  metalness: child.material.metalness,
                  aoMap: child.material.aoMap,
                  transparent: child.material.transparent,
                  alphaMap: child.material.alphaMap,
                  envMapRotation: new Euler(0, Math.PI, 0),
                  envMapIntensity: 0.8,
                })
              : new MeshBasicMaterial({ map: child.material.map })

          child.material = setMaterialTextureProperties(m as MaterialWithMap)
        }

        if (diffuse.includes('rgb')) {
          child.material.color = new Color(diffuse)
        }

        child.material = setMaterialProperties(child.material, node)
      } catch (e) {
        console.log(`failed to load texture... Id: ${nodeId} Node: ${JSON.stringify(node)}`, e)
        throw e
      }
      sceneNodes.set(nodeId, child)
    })

    return {
      modelId: nodeFile.model3d_id,
      nodeId: nodeFile.node_id,
      url: nodeFile.file.url,
      nodes,
      sceneNodes,
      animations,
      nodeNameToId,
      group,
      billboardLabels,
      meta: {
        hasLighting,
      },
    }
  } catch (err) {
    if (isDracoWASMError(err)) {
      throw new MemoryError(`${(<any>err).error}`)
    }

    const status = (<any>err).response?.status ?? (<any>err).target?.status
    if (typeof status === 'number' && status >= 400) {
      throw new GLTFMissingError(`Missing GLTF with node_id ${nodeFile.node_id} from model_id ${nodeFile.model3d_id}`)
    }

    throw err
  }
}

async function loadModelWithRetry(
  nodeFile: NodeFile,
  onProgress: (event: ProgressEvent) => void,
  { analytics, uuid }: { analytics?: typeof Analytics | null; uuid: string | null },
): Promise<ModelResponse> {
  let lastError: unknown

  for (let attempt = 1; attempt <= MODEL_LOAD_ATTEMPTS; attempt++) {
    try {
      return await loadGltf(nodeFile.file.url, onProgress, {
        analytics,
        modelId: nodeFile.model3d_id,
        uuid,
      })
    } catch (error) {
      lastError = error

      if (attempt < MODEL_LOAD_ATTEMPTS) {
        continue
      }
    }
  }

  throw lastError
}

interface CamerasCache {
  [key: number]: { data: CameraData[]; lastUpdated: number }
}

let camerasCache: CamerasCache = {}
const loadCameras = async (modelId: number, { uuid, maxAge }: LoaderConfig, primaryOnly = false): Promise<CameraData[]> => {
  if (maxAge && camerasCache[modelId] && Date.now() - camerasCache[modelId].lastUpdated < maxAge) {
    return camerasCache[modelId].data
  }

  const params: { is_primary?: boolean } = {}

  if (primaryOnly) {
    params.is_primary = true
  }

  const data = await $fetch<CameraData[]>(`v2/3dmodel/${modelId}/cameras/`, {
    ...useFetchSharedOptions(),
    params: params,
    headers: {
      ...useFetchSharedOptions().headers,
      'X-Deck-Auth-UUID': `${uuid}`,
    },
  })

  camerasCache[modelId] = { data, lastUpdated: Date.now() }

  return data
}

interface CameraCache {
  [key: string]: { data: CameraData | null; lastUpdated: number }
}

let cameraCache: CameraCache = {}
const loadCamera = async (modelId: number, cameraId: number, { uuid, maxAge }: LoaderConfig): Promise<CameraData | null> => {
  if (maxAge && cameraCache[`${modelId}-${cameraId}`] && Date.now() - cameraCache[`${modelId}-${cameraId}`].lastUpdated < maxAge) {
    return cameraCache[`${modelId}-${cameraId}`].data
  }

  const data = await $fetch<CameraData>(`v2/3dmodel/${modelId}/cameras/${cameraId}`, {
    ...useFetchSharedOptions(),
    headers: {
      ...useFetchSharedOptions().headers,
      'X-Deck-Auth-UUID': `${uuid}`,
    },
  })

  cameraCache[`${modelId}-${cameraId}`] = { data, lastUpdated: Date.now() }
  return data
}

const clearCache = () => {
  Cache.clear()
}

export const clearCameraCache = () => {
  camerasCache = {}
  cameraCache = {}
}

export { clearCache, loadCamera, loadCameras, loadModelResource }
